import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Separator } from '../../../components/ui/separator';
import { Badge } from '../../../components/ui/badge';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { formatLastUpdated } from '../Home/ActiveIssues';
import { formatReason } from '../Issues';
import { AppDispatch } from '@/redux/store';
import { useDispatch } from 'react-redux';
import { doCreateComment, doRemoveComment } from '../../../modules/issues/stores/thunk';
import { doUpdateIssue } from '../../../modules/issues/stores/thunk';
import { Bike, Hash, House, IdCard, Store, Trash2, User } from 'lucide-react';
import { useSelector } from 'react-redux';
import { ROLES } from '../../../constants';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

const IssueChat = ({ selectedIssue, userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const role = useSelector((state: any) => state.auth.user.role);
  const history = useHistory();
  const commentSectionRef = useRef(null);
  const [comment, setComment] = useState('');

  const handleOrderClick = (orderId: string, e: React.MouseEvent) => {
    const directTo = `/orders/${orderId}`;
    if (e.ctrlKey || e.metaKey) {
      window.open(directTo, '_blank');
    } else {
      history.push(directTo);
    }
  };

  const handleCommentSubmit = async () => {
    if (comment.trim()) {
      await dispatch(
        doCreateComment({
          issueId: selectedIssue.id,
          issueLongId: selectedIssue.issueId,
          message: comment,
        }),
      );

      // No need to manually fetch; Redux updates the state
      setComment(''); // Clear input field
    }
  };

  const handleStatusChange = async (status) => {
    await dispatch(doUpdateIssue({ id: selectedIssue?.id, issueId: selectedIssue.issueId, status }));
  };

  const removeComment = (comment: any, issueLongId: string) => {
    console.log({ issueLongId });
    dispatch(doRemoveComment({ commentId: comment.id, issueLongId: issueLongId }));
  };

  // Scroll to the latest comment when comments change**
  useEffect(() => {
    const scrollToBottom = () => {
      if (commentSectionRef.current) {
        const scrollElement = commentSectionRef.current.querySelector('[data-radix-scroll-area-viewport]'); // Target the inner scrolling div
        if (scrollElement) {
          setTimeout(() => {
            scrollElement.scrollTop = scrollElement.scrollHeight;
          }, 100); // Ensures content is rendered before scrolling
        }
      }
    };

    scrollToBottom();
  }, [selectedIssue?.comments]); // Watch for changes in comments

  return (
    <div className="flex flex-col h-[calc(100vh-340px)]">
      {/* Header */}
      <div className="border-b">
        <div className="flex justify-between">
          <div className="flex items-center gap-3">
            <img
              src={selectedIssue.companyLogoThumbUrl}
              alt={selectedIssue.companyName}
              className="h-6 w-6 rounded-full"
            />
            <div>
              <h2 className="text-lg font-semibold">{selectedIssue.companyName}</h2>
              <p className="text-xs text-gray-500">
                Created by {selectedIssue.creatorUserName} • {new Date(selectedIssue.createdAt).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            <div className="flex items-center">
              {role === ROLES.ADMIN && (
                <span className="flex items-center mr-2">
                  {selectedIssue.carrierId ? <Bike className="h-3 w-3 mr-1" /> : <Store className="h-3 w-3 mr-1" />}{' '}
                  shared with{' '}
                  {selectedIssue.carrierId
                    ? `carrier ${selectedIssue.carrierName}`
                    : `merchant ${selectedIssue.companyName}`}{' '}
                </span>
              )}
              <span>{formatLastUpdated(selectedIssue.updatedAt)}</span>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center mt-2 ">
          <div className="flex items-center gap-2 flex-wrap">
            {selectedIssue.orderId && (
              <Badge
                variant="outline"
                className="hover:cursor-pointer"
                onClick={(e) => handleOrderClick(selectedIssue.orderId2, e)}
              >
                <Hash className="h-3 w-3 mr-1" />
                {selectedIssue.orderId2}
              </Badge>
            )}
            {selectedIssue.orderRef && (
              <Badge variant="outline">
                <IdCard className="h-3 w-3 mr-1" /> {selectedIssue.orderRef}
              </Badge>
            )}
            {selectedIssue.deliveryName && (
              <Badge variant="outline">
                <User className="h-3 w-3 mr-1" /> {selectedIssue.deliveryName}
              </Badge>
            )}
            {selectedIssue.deliveryAddress && (
              <Badge variant="outline">
                <House className="h-3 w-3 mr-1" /> {selectedIssue.deliveryAddress}
              </Badge>
            )}
            {/* <Badge variant={selectedIssue.priority === 'HIGH' ? 'destructive' : 'default'}>
              {formatPriority(selectedIssue.priority)}
            </Badge> */}
          </div>
          <StatusMenu issue={selectedIssue} handleChange={handleStatusChange} />
        </div>
        <Separator className="my-2" />
        <p className="text-sm text-gray-700 mt-4 mb-4">
          <strong>{formatReason(selectedIssue.reason)}</strong>: {selectedIssue.message}
        </p>
      </div>

      {/* Chat Section - Fixed height with scroll when needed */}
      <div className="flex-grow overflow-hidden">
        <ScrollArea className="h-full overflow-y-auto pl-4 py-2 space-y-4" ref={commentSectionRef}>
          {selectedIssue.comments.length > 0 ? (
            [...selectedIssue.comments]
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map((comment, i) => (
                <div key={comment.id} className={`flex ${comment.userId === userId ? 'justify-end' : 'justify-start'}`}>
                  <Card className={`p-3 max-w-xs ${comment.userId === userId ? 'bg-blue-100' : 'bg-gray-100'} mb-2 `}>
                    <p className="text-xs text-gray-500">{comment.user.name}</p>
                    <p className="text-sm">{comment.message}</p>
                    <p className="text-xs text-gray-400 text-right align-top">
                      {new Date(comment.createdAt).toLocaleString('nl-NL', {
                        hour: '2-digit',
                        minute: '2-digit',
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </p>
                  </Card>

                  {role === ROLES.ADMIN && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          className={`self-center m-0 p-0 ${comment.userId !== userId || i !== selectedIssue.comments.length - 1 ? 'invisible' : ''}`}
                          size="icon"
                          variant="ghostNoBG"
                        >
                          <Trash2 />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will delete your last comment. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeComment(comment, selectedIssue.issueId)}>
                            Yes
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              ))
          ) : (
            <p className="text-gray-400 text-center">No comments yet.</p>
          )}
        </ScrollArea>
      </div>

      {/* Chat Input */}
      <div className="p-4 border-t flex items-center gap-2 bg-white">
        <Input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Write your comment..."
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && comment.trim()) {
              handleCommentSubmit();
            }
          }}
        />
        <Button onClick={handleCommentSubmit} disabled={!comment.trim()}>
          Send
        </Button>
      </div>
    </div>
  );
};

function StatusMenu({ issue, handleChange }) {
  const role = useSelector((state: any) => state.auth.user.role);

  if (role === ROLES.ADMIN) {
    return (
      <Select onValueChange={handleChange} value={issue.status}>
        <SelectTrigger className="w-[130px] h-6">
          <SelectValue placeholder={formatReason(issue.status)} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="OPEN">Open</SelectItem>
          <SelectItem value="IN_PROGRESS">In progress</SelectItem>
          <SelectItem value="RESOLVED">Resolved</SelectItem>
        </SelectContent>
      </Select>
    );
  } else {
    return <Badge variant="outline"> {formatReason(issue.status)} </Badge>;
  }
}

export default IssueChat;
